/**
 * Page-lock resolver.
 *
 * Pure functions that decide whether a given page should render its lock
 * overlay or its real content. The resolver is the single source of truth for
 * lock state — pages and route wrappers never check `import.meta.env` directly.
 *
 * Precedence (highest → lowest):
 *   1. Page must be in the manifest. If not, never locked.
 *   2. **Dev override** — `apps/stream/.dev/page-locks.json` `force[pageId]`.
 *      Only honoured when `import.meta.env.DEV` is true; stripped from prod
 *      bundles by the conditional. Used during pairing / agent sessions to
 *      flip a page's lock state without going through the UI.
 *   3. Build default: locks on. The lock screen is the canonical display
 *      for any page in the manifest.
 *
 * "Show me anyway" bypasses are intentionally NOT in this precedence list.
 * They are managed inside the `<PageLock>` component as local React state,
 * which makes them per-page and per-mount: bypassing one locked page does
 * not bypass any other locked page, and navigating away resets the bypass
 * automatically. See `src/shared/components/PageLock.tsx`.
 */

import { hasLockEntry } from './manifest'
import type { DevOverrideConfig, DevOverrideValue } from './types'

/**
 * Whether locks are on by default for any build with no per-page bypass
 * active. This is intentionally a constant `true` in both dev and prod —
 * the lock screen is the canonical state for an in-manifest page.
 */
export const BUILD_DEFAULT_LOCKS_ENABLED = true

// ── Dev override loading ─────────────────────────────────────────────────────

/**
 * Optional dev override file. `import.meta.glob` returns an empty record if
 * the file is absent, so this is safe whether or not a developer has
 * created `apps/stream/.dev/page-locks.json` locally. `.dev/` is gitignored,
 * so the file (and any edits to it) stay in the developer's working tree.
 *
 * The file is hot-reloaded by Vite — saving an edit re-evaluates this
 * module and the resolver picks up the new state on the next render.
 */
const DEV_OVERRIDE_MODULES = import.meta.glob<{ default: DevOverrideConfig }>(
  '/.dev/page-locks.json',
  { eager: true },
)

const DEV_OVERRIDE: DevOverrideConfig | null = (() => {
  // Strip the entire dev-override path in production. `import.meta.env.DEV`
  // is a static replacement, so the resolver returns an inert null in
  // prod-built bundles and tree-shakers can drop the JSON content.
  if (!import.meta.env.DEV) return null
  const first = Object.values(DEV_OVERRIDE_MODULES)[0]
  return first?.default ?? null
})()

/**
 * Read the dev-override force value for a given page, or `undefined` if no
 * override applies. Public so the settings UI can surface the active dev
 * override and so tests can assert on it.
 */
export function devOverrideFor(pageId: string): DevOverrideValue | undefined {
  if (!DEV_OVERRIDE?.force) return undefined
  const value = DEV_OVERRIDE.force[pageId]
  if (value === undefined || value === 'default') return undefined
  return value
}

/**
 * Whether any dev override is currently active in this build. Useful for
 * debugging banners and the settings UI.
 */
export function hasAnyDevOverride(): boolean {
  if (!DEV_OVERRIDE?.force) return false
  return Object.values(DEV_OVERRIDE.force).some((v) => v === 'locked' || v === 'unlocked')
}

/**
 * Whether a specific page should render its lock overlay right now,
 * ignoring any in-component "Show me anyway" bypass.
 *
 * Order: manifest gate → dev override → build default.
 *
 * Returns `false` if the page has no manifest entry, regardless of any
 * other state. Dev override is the highest-priority signal so that pairing
 * sessions and agent-driven flips do not require touching settings.
 *
 * The `<PageLock>` component overlays its own per-mount bypass on top of
 * this verdict; callers that need to know "did the user click 'Show me
 * anyway' on the rendered page" cannot tell from this function alone.
 */
export function shouldLock(pageId: string): boolean {
  if (!hasLockEntry(pageId)) return false

  const dev = devOverrideFor(pageId)
  if (dev === 'locked') return true
  if (dev === 'unlocked') return false

  return BUILD_DEFAULT_LOCKS_ENABLED
}
